import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cog8ToothIcon } from '@heroicons/react/24/outline';
import { buildJob, buildOriginTestJob, findUnsupportedCharacter } from './gcode';
import { Laser } from './laser';
import { drawPreview } from './preview';
import montserratFont from './fonts/Montserrat-400.ttf';
import cormorantFont from './fonts/CormorantGaramond-400.ttf';
import scriptFont from './fonts/PetitFormalScript-400.ttf';
import './EngravingWorkstation.css';

const API = '/api/v1/warehouse/engraving';
const FONT_URLS = {
  Montserrat: montserratFont,
  'Cormorant Garamond': cormorantFont,
  'Petit Formal Script': scriptFont,
};
const DEFAULT_PRESET = {
  laser: 'blue',
  power_pct: 30,
  depth_pct: 10,
  passes: 1,
  line_interval_mm: 0.08,
  origin_x_mm: 80,
  origin_y_mm: 60,
  text_height_mm: 4,
  max_width_mm: 40,
  rotation_deg: 0,
  mirror_x: false,
  mirror_y: true,
  outline_pass: true,
};
const PRESET_FIELDS = [
  ['laser', 'Laser', 'select'],
  ['power_pct', 'Power (%)', 'number'],
  ['depth_pct', 'Depth (%)', 'number'],
  ['passes', 'Passes', 'number'],
  ['line_interval_mm', 'Line interval (mm)', 'number'],
  ['origin_x_mm', 'Origin X (mm)', 'number'],
  ['origin_y_mm', 'Origin Y (mm)', 'number'],
  ['text_height_mm', 'Text height (mm)', 'number'],
  ['max_width_mm', 'Max width (mm)', 'number'],
  ['rotation_deg', 'Rotation (°)', 'number'],
  ['mirror_x', 'Mirror X', 'checkbox'],
  ['mirror_y', 'Mirror Y', 'checkbox'],
  ['outline_pass', 'Outline pass', 'checkbox'],
];
const fontBufferCache = new Map();
const COLOURS = {
  Sapphire: '#1f3f73',
  Gunmetal: '#4a4d52',
  Saffron: '#c8862d',
  Bourbon: '#7a3f1d',
  'Onyx Black': '#1b1b1b',
  Sage: '#8a9a7b',
  'Blue Haven': '#2f5d7c',
};
const itemKey = (item) => `${item.orderName}:${item.lineItemId}:${item.unit}`;
const fontClass = (font) => ({
  Montserrat: 'engraving-font-montserrat',
  'Cormorant Garamond': 'engraving-font-cormorant',
  'Petit Formal Script': 'engraving-font-script',
}[font] || 'engraving-font-montserrat');
const formatPortId = (value) => Number.isInteger(value) ? `0x${value.toString(16).padStart(4, '0')}` : 'unknown';
const escapeRawReply = (reply) => Array.from(reply, (character) => {
  const code = character.charCodeAt(0);
  if (code === 13) return '\\r';
  if (code === 10) return '\\n';
  if (code < 32 || (code >= 127 && code <= 159)) return `\\x${code.toString(16).padStart(2, '0')}`;
  return character;
}).join('').replace(/ +$/g, (spaces) => '\\x20'.repeat(spaces.length));

const formatLaserDiagnostics = (diagnostics) => {
  const portInfo = diagnostics.portInfo || {};
  return [
    `Port VID ${formatPortId(portInfo.usbVendorId)} · PID ${formatPortId(portInfo.usbProductId)}`,
    `Baud ${diagnostics.baudRate} · DTR ${diagnostics.dtr ? 'on' : 'off'}`,
    `Status query: ${diagnostics.statusQuery === null ? 'none answered (Engrave locked)' : `${diagnostics.statusQuery} (auto-detected)`}`,
    ...diagnostics.queries.map(({ query, outcome, ms, bytes }) => `${query} → ${outcome === 'timeout' ? `timeout after ${ms} ms` : `${outcome} after ${ms} ms`} · ${bytes} bytes`),
    `Total bytes received since connect: ${diagnostics.totalBytes}`,
    `Text: ${diagnostics.text || '(empty)'}`,
    `Hex: ${diagnostics.hex || '(empty)'}`,
    `Pending buffer: ${diagnostics.pendingBuffer || '(empty)'}`,
    `Raw replies:\n${diagnostics.rawReplies.length ? diagnostics.rawReplies.map(escapeRawReply).join('\n') : '(none)'}`,
  ].join('\n');
};

const apiRequest = async (path, options = {}) => {
  const response = await fetch(`${API}${path}`, {
    credentials: 'include',
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return body;
};

const loadFontBuffer = async (fontName) => {
  const url = FONT_URLS[fontName] || FONT_URLS.Montserrat;
  if (!fontBufferCache.has(url)) {
    fontBufferCache.set(url, fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Could not load ${fontName} font.`);
      return response.arrayBuffer();
    }));
  }
  return fontBufferCache.get(url);
};

const EngravingWorkstation = ({ canEdit = false }) => {
  const scanInputRef = useRef(null);
  const previewRef = useRef(null);
  const laserRef = useRef(null);
  const jobRef = useRef(null);
  const startingRef = useRef(false);
  const infoReadingRef = useRef(false);
  const [stationName, setStationName] = useState(() => localStorage.getItem('warehouse-engraving-station') || 'Station 2');
  const [laserSnapshot, setLaserSnapshot] = useState({ connected: false, busy: false, machineState: null, rawReplies: [] });
  const [orders, setOrders] = useState([]);
  const [tote, setTote] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [barcode, setBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [glyphError, setGlyphError] = useState('');
  const [glyphLoading, setGlyphLoading] = useState(false);
  const [laserIdle, setLaserIdle] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [presetDraft, setPresetDraft] = useState(DEFAULT_PRESET);
  const [savedState, setSavedState] = useState('');
  const [infoBusy, setInfoBusy] = useState(false);
  const [laserInfo, setLaserInfo] = useState(null);
  const [copyState, setCopyState] = useState('');

  const items = useMemo(() => orders.flatMap((order) =>
    (order.items || []).map((item) => ({ ...item, orderName: order.orderName }))), [orders]);
  const selectedItem = items.find((item) => itemKey(item) === selectedKey) || null;
  const engravingCount = items.length;
  const remainingCount = items.filter((item) => !item.engravedAt).length;
  const currentPreset = selectedItem?.preset || null;
  const laserInfoText = useMemo(() => laserInfo ? formatLaserDiagnostics(laserInfo) : '', [laserInfo]);
  const draftHasChanges = selectedItem
    ? JSON.stringify(presetDraft) !== JSON.stringify({ ...DEFAULT_PRESET, ...(selectedItem.preset || {}) })
    : true;

  const syncLaser = useCallback((laser) => {
    setLaserSnapshot({
      connected: laser.connected,
      busy: laser.busy,
      machineState: laser.machineState,
      rawReplies: [...laser.rawReplies].slice(-12),
    });
  }, []);

  useEffect(() => {
    const laser = new Laser();
    laserRef.current = laser;
    const unsubscribe = laser.subscribe(syncLaser);
    syncLaser(laser);
    const focusTimer = window.setTimeout(() => scanInputRef.current?.focus(), 200);
    return () => {
      window.clearTimeout(focusTimer);
      unsubscribe();
    };
  }, [syncLaser]);

  useEffect(() => {
    localStorage.setItem('warehouse-engraving-station', stationName);
  }, [stationName]);

  useEffect(() => {
    const preset = selectedItem?.preset;
    setPresetDraft({ ...DEFAULT_PRESET, ...(preset || {}) });
    setSavedState('');
  }, [selectedItem?.lineItemId, selectedItem?.unit, selectedItem?.handle, selectedItem?.preset]);

  useEffect(() => {
    let active = true;
    setGlyphError('');
    if (!selectedItem?.text) {
      setGlyphLoading(false);
      return () => { active = false; };
    }
    setGlyphLoading(true);
    loadFontBuffer(selectedItem.font).then((fontBuffer) => {
      if (!active) return;
      const unsupported = findUnsupportedCharacter(fontBuffer, selectedItem.text);
      setGlyphError(unsupported ? `This font can't engrave '${unsupported}'` : '');
    }).catch((error) => {
      if (active) setGlyphError(error.message || 'Font could not be loaded.');
    }).finally(() => {
      if (active) setGlyphLoading(false);
    });
    return () => { active = false; };
  }, [selectedItem?.text, selectedItem?.font]);

  useEffect(() => {
    if (!selectedItem || !previewRef.current) return;
    const canvas = previewRef.current;
    if (selectedItem.placement && selectedItem.previewImage) {
      drawPreview(canvas, selectedItem.text, selectedItem.font, selectedItem.placement, selectedItem.previewImage);
    }
  }, [selectedItem]);

  const focusScan = () => window.setTimeout(() => scanInputRef.current?.focus(), 0);

  const refreshIdle = useCallback(async () => {
    if (infoReadingRef.current) return;
    const laser = laserRef.current;
    if (!laser?.connected || laser.busy || jobRef.current) {
      setLaserIdle(false);
      return;
    }
    try {
      setLaserIdle(await laser.isIdle());
    } catch {
      setLaserIdle(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(refreshIdle, 1800);
    return () => window.clearInterval(timer);
  }, [refreshIdle]);

  const loadTote = async (event) => {
    event?.preventDefault();
    const code = barcode.trim();
    if (!code) return;
    setLoading(true);
    setFeedback('');
    try {
      const result = await apiRequest(`/tote/${encodeURIComponent(code)}?station=${encodeURIComponent(stationName)}`);
      let presetRows = [];
      try {
        const presets = await apiRequest(`/presets?station=${encodeURIComponent(stationName)}`);
        presetRows = Array.isArray(presets) ? presets : (presets.presets || []);
      } catch (_error) {
        presetRows = [];
      }
      const nextOrders = (result.orders || []).map((order) => ({
        ...order,
        items: (order.items || []).map((item) => ({
          ...item,
          preset: item.preset || presetRows.find((preset) => preset.product_handle === item.handle) || null,
        })),
      }));
      setTote(result.tote);
      setOrders(nextOrders);
      const first = nextOrders.flatMap((order) => (order.items || []).map((item) => ({ ...item, orderName: order.orderName })))
        .find((item) => !item.engravedAt);
      setSelectedKey(first ? itemKey(first) : null);
      setBarcode('');
    } catch (error) {
      setTote(null);
      setOrders([]);
      setSelectedKey(null);
      setFeedback(error.status === 503
        ? 'Add the station key to load orders'
        : error.message || 'Could not load this tote.');
    } finally {
      setLoading(false);
      focusScan();
    }
  };

  const pairLaser = async (showAll = false) => {
    try {
      await laserRef.current?.requestPort(showAll);
      setFeedback('');
      await refreshIdle();
    } catch (error) {
      if (error.name !== 'NotFoundError') setFeedback(error.message || 'Could not pair the laser.');
    } finally {
      focusScan();
    }
  };

  const readLaserInfo = async () => {
    if (!laserRef.current?.connected) return;
    infoReadingRef.current = true;
    setInfoBusy(true);
    try {
      setLaserInfo(await laserRef.current.readInfo());
      setCopyState('');
    } catch (error) {
      setFeedback(error.message || 'Could not read laser information.');
    } finally {
      infoReadingRef.current = false;
      setInfoBusy(false);
      await refreshIdle();
      focusScan();
    }
  };

  const copyLaserInfo = async () => {
    try {
      await navigator.clipboard.writeText(laserInfoText);
      setCopyState('Copied');
    } catch {
      setCopyState('Copy failed');
    }
  };

  const updateItem = (key, changes) => {
    setOrders((current) => current.map((order) => ({
      ...order,
      items: order.items.map((item) => itemKey({ ...item, orderName: order.orderName }) === key
        ? { ...item, ...changes }
        : item),
    })));
  };

  const postJob = async (item, status) => apiRequest('/jobs', {
    method: 'POST',
    body: JSON.stringify({
      station: stationName,
      orderName: item.orderName,
      lineItemId: item.lineItemId,
      unit: item.unit,
      sku: item.sku,
      text: item.text,
      font: item.font,
      status,
    }),
  });

  const startJob = async (type = 'engrave') => {
    const laser = laserRef.current;
    const test = type !== 'engrave';
    const item = selectedItem;
    const preset = test ? presetDraft : currentPreset;
    if (!laser?.connected || laser.busy || jobRef.current || startingRef.current || !preset || (!test && !item?.placement)) return;
    if (!test && item.engravedAt && !window.confirm(`Engrave "${item.text}" again?`)) return;
    startingRef.current = true;
    setStarting(true);
    try {
      if (!await laser.isIdle()) {
        setLaserIdle(false);
        setFeedback('The laser is not idle. Check the machine before engraving.');
        return;
      }
      let jobData;
      if (type === 'origin') {
        jobData = buildOriginTestJob({ preset });
      } else {
        const text = type === 'scrap' ? 'TEST' : item.text;
        const fontName = type === 'scrap' ? 'Montserrat' : item.font;
        const fontBuffer = await loadFontBuffer(fontName);
        const unsupported = findUnsupportedCharacter(fontBuffer, text);
        if (unsupported) throw new Error(`This font can't engrave '${unsupported}'`);
        if (!test && text.length > Number(item.placement?.laserMax)) {
          throw new Error(`Text exceeds the ${item.placement.laserMax}-character laser limit.`);
        }
        jobData = buildJob({ fontBuffer, text, preset });
      }
      const runningJob = { item, test, type, progress: 0, stopped: false };
      jobRef.current = runningJob;
      setJob(runningJob);
      setFeedback('');
      await laser.run(jobData.lines, (progress) => {
        const next = { ...runningJob, progress };
        jobRef.current = next;
        setJob(next);
      });
      if (!test) {
        const engravedAt = new Date().toISOString();
        const key = itemKey(item);
        updateItem(key, { engravedAt });
        try {
          await postJob(item, 'engraved');
        } catch (_error) {
          setFeedback('Engraving finished, but its job log could not be saved.');
        }
        const next = items.find((candidate) => !candidate.engravedAt && itemKey(candidate) !== key);
        setSelectedKey(next ? itemKey(next) : null);
      }
    } catch (error) {
      if (!jobRef.current?.stopped) setFeedback(error.message || 'Engraving failed.');
    } finally {
      startingRef.current = false;
      setStarting(false);
      jobRef.current = null;
      setJob(null);
      setLaserIdle(false);
      await refreshIdle();
      focusScan();
    }
  };

  const stopJob = async () => {
    const runningJob = jobRef.current;
    if (!runningJob) return;
    runningJob.stopped = true;
    try {
      await laserRef.current?.stop();
      if (!runningJob.test && runningJob.item) {
        try {
          await postJob(runningJob.item, 'stopped');
        } catch (_error) {
          setFeedback('Laser stopped, but its job log could not be saved.');
        }
      }
    } finally {
      focusScan();
    }
  };

  const savePreset = async () => {
    if (!canEdit || !selectedItem?.handle) return;
    setSavedState('saving');
    try {
      const result = await apiRequest('/presets', {
        method: 'PUT',
        body: JSON.stringify({
          station: stationName,
          product_handle: selectedItem.handle,
          ...presetDraft,
        }),
      });
      const preset = result.preset || result;
      updateItem(itemKey(selectedItem), { preset });
      setSavedState('saved');
    } catch (error) {
      setSavedState(error.message || 'Could not save preset.');
    } finally {
      focusScan();
    }
  };

  const engraveDisabled = !selectedItem
    || !currentPreset
    || !selectedItem.placement
    || String(selectedItem.text || '').length > Number(selectedItem.placement?.laserMax)
    || !laserSnapshot.connected
    || !laserIdle
    || glyphLoading
    || Boolean(glyphError)
    || Boolean(starting)
    || Boolean(job);
  const unavailableReason = selectedItem && !currentPreset
    ? 'Save a preset before engraving.'
    : selectedItem && !selectedItem.placement
      ? 'This product has no laser placement configured.'
      : selectedItem && String(selectedItem.text || '').length > Number(selectedItem.placement?.laserMax)
        ? `Text exceeds the ${selectedItem.placement.laserMax}-character laser limit.`
        : glyphError;
  const busyLabel = job?.type === 'origin' ? 'Testing origin…' : job?.type === 'scrap' ? 'Testing on scrap…' : 'Engraving…';

  return (
    <div className="engraving-workstation min-h-screen px-5 py-5 lg:px-8">
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">Warehouse · Engraving</div>
          <h1 className="mt-1 text-2xl font-semibold text-gray-900">{tote ? `Tote ${tote.name}` : 'Engraving workstation'}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {tote ? `${orders.length} ${orders.length === 1 ? 'order' : 'orders'} · ${engravingCount} ${engravingCount === 1 ? 'engraving' : 'engravings'}` : 'Scan a tote to load its engraving work.'}
          </p>
        </div>
        <button type="button" onClick={() => setDrawerOpen(true)} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50" aria-label="Station setup" title="Station setup"><Cog8ToothIcon className="h-5 w-5" /></button>
      </header>

      <form onSubmit={loadTote} className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        <label className="sr-only" htmlFor="engraving-tote-scan">Scan tote</label>
        <input ref={scanInputRef} id="engraving-tote-scan" value={barcode} onChange={(event) => setBarcode(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') loadTote(event); }}
          placeholder="Scan tote" autoComplete="off" className="engraving-scan-input min-w-[240px] flex-1 rounded-lg border border-gray-300 px-4 py-3 text-base" />
        <button type="submit" disabled={loading || !barcode.trim()} className="rounded-lg bg-gray-900 px-5 py-3 font-semibold text-white disabled:opacity-45">{loading ? 'Loading…' : 'Load tote'}</button>
        <div className={`ml-auto flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium ${job ? 'bg-amber-50 text-amber-800' : laserSnapshot.connected ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
          <span className={`h-2 w-2 rounded-full ${job ? 'bg-amber-500' : laserSnapshot.connected ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          {job ? busyLabel : laserSnapshot.connected ? (laserIdle ? 'Laser ready' : 'Laser connected · checking') : 'Laser not paired'}
          {!laserSnapshot.connected && <button type="button" onClick={() => pairLaser(false)} className="ml-1 underline underline-offset-2">Pair laser</button>}
        </div>
      </form>

      {feedback && <div role="alert" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">{feedback}</div>}

      {!laserSnapshot.connected && !navigator.serial && <div className="mt-2 text-sm text-gray-500">Use desktop Chrome to engrave.</div>}

      <main className="mt-5 grid min-h-[560px] gap-5 lg:grid-cols-[minmax(360px,0.9fr)_minmax(440px,1.1fr)]">
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <h2 className="font-semibold text-gray-900">Tote engraving list</h2>
            <div className="mt-1 text-xs text-gray-500">{tote?.barcode || 'Scan tote barcode to begin'}</div>
          </div>
          <div className="divide-y divide-gray-100">
            {orders.map((order) => (
              <div key={order.orderName}>
                <div className="bg-gray-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{order.orderName}</div>
                {(order.items || []).map((item) => {
                  const row = { ...item, orderName: order.orderName };
                  const key = itemKey(row);
                  const selected = key === selectedKey;
                  return (
                    <button key={key} type="button" onClick={() => { setSelectedKey(key); focusScan(); }}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 ${selected ? 'bg-blue-50/70' : 'bg-white'}`}>
                      {item.previewImage
                        ? <div className="relative h-14 w-24 shrink-0 overflow-hidden rounded-md bg-gray-100"><img src={item.previewImage} alt="" className="h-full w-full object-cover" /><span className={`absolute inset-0 grid place-items-center text-sm text-black/80 ${fontClass(item.font)}`}>{item.text}</span></div>
                        : <div className="engraving-preview-empty h-14 w-24 shrink-0 rounded-md" />}
                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-lg text-gray-900 ${fontClass(item.font)}`}>{item.text}</div>
                        <div className="mt-1 truncate text-xs text-gray-500">{item.font} · {item.productTitle} · {item.colour} · {item.sku}</div>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-medium ${item.engravedAt ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{item.engravedAt ? 'Engraved' : 'To engrave'}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {!tote && !feedback && <div className="px-6 py-16 text-center text-sm text-gray-500">Scan a tote to load engraving units.</div>}
          {tote && engravingCount === 0 && <div className="px-6 py-16 text-center text-sm text-gray-500">Nothing to engrave in this tote.</div>}
          {feedback === 'Add the station key to load orders' && <div className="px-6 py-12 text-center text-sm text-gray-600">Add the station key to load orders</div>}
        </section>

        <section className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          {selectedItem ? (
            <>
              <div className="mb-4 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Engraving detail · {selectedItem.orderName}</div>
                <span className="text-xs text-gray-500">{selectedItem.sku}</span>
              </div>
              {selectedItem.placement && selectedItem.previewImage
                ? <canvas ref={previewRef} className="engraving-preview-canvas" aria-label={`Preview of ${selectedItem.text}`} />
                : <div className="engraving-preview-empty grid place-items-center rounded-lg text-sm text-gray-500">Preview unavailable</div>}
              <div className="mt-5 text-center">
                <div className={`text-4xl text-gray-900 ${fontClass(selectedItem.font)}`}>{selectedItem.text}</div>
                <div className="mt-2 text-sm text-gray-500">{selectedItem.font}</div>
              </div>
              <div className="mt-5 flex items-center justify-center gap-2 text-sm text-gray-700">
                <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: COLOURS[selectedItem.colour] || '#9ca3af' }} />
                {selectedItem.productTitle} · {selectedItem.colour}
              </div>
              <div className="mt-1 text-center text-xs text-gray-500">{selectedItem.sku} · Order {selectedItem.orderName}</div>
              {glyphLoading && <p className="mt-4 text-center text-sm text-gray-500">Checking font glyphs…</p>}
              {unavailableReason && <p className="mt-4 text-center text-sm text-amber-700">{unavailableReason}</p>}
              <div className="mt-auto flex flex-col gap-3 pt-6">
                <button type="button" disabled title="Physical preview isn't available yet" aria-describedby="engraving-preview-note" className="w-full rounded-lg border border-gray-900 bg-white px-5 py-4 text-lg font-semibold tracking-wider text-gray-900 disabled:cursor-not-allowed disabled:opacity-40">PREVIEW</button>
                {job
                  ? <button type="button" onClick={stopJob} className="w-full rounded-lg bg-red-600 px-5 py-4 text-lg font-semibold text-white">STOP</button>
                  : <button type="button" disabled={engraveDisabled} onClick={() => startJob('engrave')} className="w-full rounded-lg bg-gray-900 px-5 py-4 text-lg font-semibold tracking-wider text-white disabled:cursor-not-allowed disabled:opacity-40">ENGRAVE</button>}
              </div>
              <p id="engraving-preview-note" className="mt-3 text-center text-xs text-gray-500">Preview on the wallet stays off until a non-burning LP4 outline is tested. Use the on-screen preview above.</p>
            </>
          ) : (
            <div className="grid flex-1 place-items-center text-center text-gray-500">
              <div><div className="text-4xl">⌁</div><div className="mt-3 font-medium">{tote ? 'Nothing to engrave in this tote.' : 'Scan tote to begin'}</div>
                {job && <button type="button" onClick={stopJob} className="mt-5 rounded-lg bg-red-600 px-6 py-3 font-semibold text-white">STOP</button>}
              </div>
            </div>
          )}
        </section>
      </main>

      <footer className="mt-4 rounded-lg bg-white px-5 py-3 text-sm font-medium text-gray-600">
        {tote && remainingCount === 0 && engravingCount > 0
          ? 'All engraved. Keep packing in ShipHero.'
          : `${remainingCount} left in this tote`}
      </footer>

      {drawerOpen && (
        <div className="engraving-drawer-backdrop fixed inset-0 z-50 flex justify-end" onMouseDown={(event) => { if (event.target === event.currentTarget) setDrawerOpen(false); }}>
          <aside className="engraving-drawer flex flex-col bg-white shadow-2xl" aria-label="Station setup">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div><div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Station setup</div><h2 className="mt-1 text-lg font-semibold">Laser & preset</h2></div>
              <button type="button" onClick={() => setDrawerOpen(false)} className="rounded p-2 text-gray-500 hover:bg-gray-100" aria-label="Close setup">×</button>
            </div>
            <div className="space-y-5 overflow-y-auto px-5 py-5">
              <label className="block text-sm font-medium text-gray-700">Station name
                <input value={stationName} onChange={(event) => setStationName(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => pairLaser(false)} className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white">Pair laser</button>
                <button type="button" onClick={() => pairLaser(true)} className="text-xs text-gray-600 underline">Show all devices</button>
                <button type="button" disabled={!laserSnapshot.connected || laserSnapshot.busy || infoBusy} onClick={readLaserInfo} className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-40">{infoBusy ? 'Reading…' : 'Read $I and ?'}</button>
              </div>
              {!navigator.serial && <p className="text-sm text-amber-700">Use desktop Chrome to engrave.</p>}
              {laserInfo && (
                <section className="rounded-md border border-gray-200">
                  <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
                    <h3 className="text-sm font-medium text-gray-700">Laser info</h3>
                    <button type="button" onClick={copyLaserInfo} className="text-xs font-medium text-gray-600 underline">{copyState || 'Copy'}</button>
                  </div>
                  <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap break-all bg-gray-900 p-3 font-mono text-xs text-green-200">{laserInfoText}</pre>
                </section>
              )}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between">
                  <div><h3 className="font-semibold text-gray-900">{selectedItem ? selectedItem.productTitle : 'Laser test'}</h3><p className="mt-1 text-xs text-gray-500">{selectedItem ? selectedItem.handle : 'Standalone test preset'}</p></div>
                  {savedState === 'saved' && <span className="text-sm text-emerald-700">Saved ✓</span>}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {PRESET_FIELDS.map(([field, label, type]) => (
                    <label key={field} className={`text-xs font-medium text-gray-600 ${type === 'checkbox' ? 'flex items-center gap-2 rounded-md border border-gray-200 px-2 py-2' : ''}`}>
                      {type === 'checkbox'
                        ? <><input type="checkbox" checked={Boolean(presetDraft[field])} disabled={!canEdit} onChange={(event) => setPresetDraft((current) => ({ ...current, [field]: event.target.checked }))} />{label}</>
                        : <>{label}{type === 'select'
                          ? <select value={presetDraft[field]} disabled={!canEdit} onChange={(event) => setPresetDraft((current) => ({ ...current, [field]: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"><option value="blue">Blue</option><option value="ir">IR</option></select>
                          : <input type="number" step={field === 'line_interval_mm' ? '0.01' : '1'} value={presetDraft[field]} disabled={!canEdit} onChange={(event) => setPresetDraft((current) => ({ ...current, [field]: Number(event.target.value) }))} className="no-arrows-by-input engraving-number-input mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm" />}</>}
                    </label>
                  ))}
                </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={!laserSnapshot.connected || (job ? job.type !== 'origin' : (!laserIdle || starting))} onClick={() => job?.type === 'origin' ? stopJob() : startJob('origin')} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-40">{job?.type === 'origin' ? 'STOP' : 'Test origin'}</button>
              <button type="button" disabled={!laserSnapshot.connected || (job ? job.type !== 'scrap' : (!laserIdle || starting))} onClick={() => job?.type === 'scrap' ? stopJob() : startJob('scrap')} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-40">{job?.type === 'scrap' ? 'STOP' : 'Test on scrap'}</button>
                </div>
                {canEdit && selectedItem && <div className="mt-4 flex items-center gap-3">
                  <button type="button" disabled={savedState === 'saving' || (!draftHasChanges && savedState === 'saved')} onClick={savePreset} className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{savedState === 'saving' ? 'Saving…' : 'Save preset'}</button>
                  {savedState && savedState !== 'saved' && savedState !== 'saving' && <span role="alert" className="text-xs text-red-700">{savedState}</span>}
                </div>}
                {!canEdit && <p className="mt-3 text-xs text-gray-500">View only. Warehouse engraving edit access is required to change presets.</p>}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default EngravingWorkstation;
